/**
 * Persona detay ekranı — kimliğin tek yönetim yüzeyi.
 *
 * Sekmeler bilinçli olarak personanın "katmanlarına" karşılık gelir ve
 * system prompt'a girme sıralarıyla aynı düzendedir:
 *   Kimlik   → charter + talimat (prompt'un gövdesi)
 *   Mizaç    → psikometrik davranış kuralları
 *   Hafıza   → geçmiş tartışmalardan hatırladıkları
 *   Arşiv    → kişisel belgeleri + yazım sesi
 *   Gelişim  → geri bildirimden öğrendiği dersler
 *   Geçmiş   → düzenleme sürümleri (geri alma)
 *
 * Kaydetme yalnızca Kimlik/Mizaç sekmelerinde vardır; diğer sekmeler
 * kendi mutasyonlarını anında uygular çünkü hepsi ayrı kaynaklardır
 * (hafıza notu, belge, ders) ve tek bir "Kaydet" düğmesine bağlamak
 * kullanıcının yarım kalan işini kaybetmesine yol açardı.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  GrowthLesson,
  LibraryCatalog,
  LibraryPersona,
  MemoryCollection,
  PersonaDetailBundle,
  PersonaRevision,
  PersonaUnit,
  PsychometricAxis,
  PsychometricProfile,
  StyleProfile,
} from "../../domain/personaLibrary";
import { useModelsCatalog } from "../../hooks/useModelsCatalog";
import { approvalRate, unitPathLabel } from "../../domain/personaLibrary";
import {
  LibraryApiError,
  clonePersona,
  deletePersona,
  fetchCatalog,
  fetchPersona,
  listRevisions,
  refineInstruction,
  revertRevision,
  updatePersona,
  uploadAvatar,
} from "../../transport/personaLibraryClient";
import { ArchiveTab } from "./ArchiveTab";
import { DemographicsEditor } from "./DemographicsEditor";
import { GrowthTab } from "./GrowthTab";
import { MemoryTab } from "./MemoryTab";
import { roleLabel } from "./PersonaComposer";
import { PsychometricEditor } from "./PsychometricEditor";
import {
  Btn,
  Chip,
  ErrorBanner,
  Field,
  Modal,
  PersonaAvatar,
  SectionTitle,
  Select,
  TextArea,
  TextInput,
  Toggle,
} from "./ui";

type Tab =
  | "identity"
  | "biography"
  | "temperament"
  | "memory"
  | "archive"
  | "growth"
  | "history";

const TABS: { key: Tab; label: string }[] = [
  { key: "identity", label: "Kimlik" },
  { key: "biography", label: "Özgeçmiş" },
  { key: "temperament", label: "Mizaç" },
  { key: "memory", label: "Hafıza" },
  { key: "archive", label: "Arşiv" },
  { key: "growth", label: "Gelişim" },
  { key: "history", label: "Geçmiş" },
];

export function PersonaDetail({
  personaId,
  catalog,
  units,
  onClose,
  onMutated,
}: {
  personaId: string;
  catalog: LibraryCatalog | null;
  units: PersonaUnit[];
  onClose: () => void;
  onMutated: () => void;
}) {
  const [bundle, setBundle] = useState<PersonaDetailBundle | null>(null);
  const [tab, setTab] = useState<Tab>("identity");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Kimlik + mizaç sekmeleri için yerel taslak. Sunucudan gelen `bundle`
  // ile ayrı tutulur ki "Kaydet"e basılmadan yapılan değişiklikler bir
  // arka plan tazelemesinde kaybolmasın.
  const [draft, setDraft] = useState<LibraryPersona | null>(null);
  const [saving, setSaving] = useState(false);

  // Eksen kataloğu için YEREL yedek. Sayfa store'u katalogu bir sebeple
  // getirememişse (bkz. rota gölgeleme olayı) bu ekran kendi başına
  // toparlayabilmeli — mizaç düzenleyicinin varlığı, ilgisiz bir listeleme
  // çağrısının başarısına bağlı olmamalı.
  const [localAxes, setLocalAxes] = useState<PsychometricAxis[]>([]);
  const [axesError, setAxesError] = useState<string | null>(null);
  const axes = catalog?.axes?.length ? catalog.axes : localAxes;

  const loadAxes = useCallback(async () => {
    setAxesError(null);
    try {
      const fresh = await fetchCatalog();
      setLocalAxes(fresh.axes);
    } catch (err) {
      setAxesError(err instanceof LibraryApiError ? err.message : "bilinmeyen hata");
    }
  }, []);

  useEffect(() => {
    if (!catalog?.axes?.length && localAxes.length === 0) void loadAxes();
  }, [catalog, localAxes.length, loadAxes]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPersona(personaId);
      setBundle(data);
      setDraft(data.persona);
      setError(null);
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Persona yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [personaId]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(() => {
    if (!bundle || !draft) return false;
    return JSON.stringify(bundle.persona) !== JSON.stringify(draft);
  }, [bundle, draft]);

  /**
   * Kaydet — başarılıysa modalı KAPATIR.
   *
   * Kullanıcı bildirimi: "kaydet dediğimde modal kapanmıyor". Eski davranış
   * yalnız kaydedip açık kalıyordu; düğme pasifleşiyor ama hiçbir şey
   * olmamış gibi görünüyordu, dolayısıyla kaydın işlenip işlenmediği
   * belirsizdi. `keepOpen` yalnız iç akışlar (sürüm geri alma gibi) için.
   */
  async function save(opts?: { keepOpen?: boolean }) {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updatePersona(personaId, {
        title: draft.title,
        displayName: draft.displayName,
        role: draft.role,
        expertise: draft.expertise ?? "",
        summary: draft.summary,
        charter: draft.charter,
        systemInstruction: draft.systemInstruction,
        tags: draft.tags,
        psychometrics: draft.psychometrics,
        modelId: draft.modelId ?? "",
        unitId: draft.unitId,
        demographics: draft.demographics,
        gender: draft.gender ?? "",
        samplingOverrides: draft.samplingOverrides,
        memoryConfig: draft.memoryConfig,
        voiceRate: draft.voiceRate,
        voicePitch: draft.voicePitch,
        isArchived: draft.isArchived,
      });
      setBundle((prev) => (prev ? { ...prev, persona: updated } : prev));
      setDraft(updated);
      onMutated();
      if (!opts?.keepOpen) onClose();
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  function patch(next: Partial<LibraryPersona>) {
    setDraft((prev) => (prev ? { ...prev, ...next } : prev));
  }

  /**
   * Kapatma isteği — kaydedilmemiş değişiklik varsa onay ister.
   *
   * `Modal` overlay tıklaması ve Escape de buradan geçer; aksi hâlde
   * uzun bir talimat düzenlemesi yanlışlıkla bir tıklamayla kaybolur.
   */
  function requestClose() {
    if (saving) return;
    if (dirty && !confirm("Kaydedilmemiş değişiklikler var. Yine de kapatılsın mı?")) {
      return;
    }
    onClose();
  }

  const persona = draft;

  return (
    <Modal
      open
      onClose={requestClose}
      title={persona?.title ?? "Persona"}
      subtitle={
        persona
          ? `${persona.displayName}${persona.expertise ? ` — ${persona.expertise}` : ""}`
          : undefined
      }
      width="max-w-5xl"
      footer={
        tab === "identity" || tab === "temperament" || tab === "biography" ? (
          <>
            {dirty && (
              <span className="mr-auto text-[11.5px] text-amber-300">
                Kaydedilmemiş değişiklikler var
              </span>
            )}
            <Btn tone="ghost" onClick={requestClose} disabled={saving}>
              Kapat
            </Btn>
            <Btn
              tone="primary"
              onClick={() => void save()}
              loading={saving}
              disabled={!dirty}
            >
              Kaydet ve Kapat
            </Btn>
          </>
        ) : (
          <Btn tone="ghost" onClick={requestClose}>
            Kapat
          </Btn>
        )
      }
    >
      {loading || !persona || !bundle ? (
        <div className="py-12 text-center text-[12.5px] text-slate-500">Yükleniyor…</div>
      ) : (
        <div className="space-y-4">
          {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

          <HeaderStrip
            persona={persona}
            unitPath={unitPathLabel(units, persona.unitId)}
            personaId={personaId}
            onAvatarChanged={(url) => {
              patch({ avatarUrl: url });
              setBundle((prev) =>
                prev ? { ...prev, persona: { ...prev.persona, avatarUrl: url } } : prev,
              );
              onMutated();
            }}
            onError={setError}
            onCloned={onMutated}
            onDeleted={() => {
              onMutated();
              onClose();
            }}
          />

          <nav className="flex flex-wrap gap-1 rounded-xl border border-white/[0.06] bg-white/[0.02] p-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={[
                  "rounded-lg px-3 py-1.5 text-[12px] font-medium transition-colors",
                  tab === t.key
                    ? "bg-white/[0.09] text-white"
                    : "text-slate-400 hover:bg-white/[0.04] hover:text-white",
                ].join(" ")}
              >
                {t.label}
                {t.key === "growth" && bundle.pendingFeedback > 0 && (
                  <span className="ml-1.5 rounded-full bg-amber-500/25 px-1.5 text-[10px] text-amber-200">
                    {bundle.pendingFeedback}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {tab === "identity" && (
            <IdentityTab
              persona={persona}
              catalog={catalog}
              units={units}
              personaId={personaId}
              onPatch={patch}
              onError={setError}
            />
          )}

          {tab === "biography" && (
            <DemographicsEditor
              value={persona.demographics}
              gender={persona.gender}
              options={catalog?.demographicOptions ?? null}
              onChange={(next) => patch({ demographics: next })}
              onGenderChange={(next) => patch({ gender: next })}
            />
          )}

          {tab === "temperament" &&
            (axes.length > 0 ? (
              <PsychometricEditor
                axes={axes}
                profile={persona.psychometrics}
                onChange={(next: PsychometricProfile) => patch({ psychometrics: next })}
              />
            ) : (
              // BULUNAN HATA (canlı): burada `catalog && (...)` vardı ve
              // katalog yüklenemediğinde sekme SESSİZCE boş render ediyordu —
              // kullanıcı "Mizaç bölümü yok" olarak görüyordu. Artık ya
              // yerel bir yeniden deneme yapılır ya da nedeni yazılır.
              <div className="space-y-3 py-6 text-center">
                <p className="text-[12.5px] text-slate-400">
                  {axesError
                    ? `Mizaç eksenleri yüklenemedi: ${axesError}`
                    : "Mizaç eksenleri yükleniyor…"}
                </p>
                {axesError && (
                  <Btn tone="outline" onClick={() => void loadAxes()}>
                    Tekrar Dene
                  </Btn>
                )}
              </div>
            ))}

          {tab === "memory" && (
            <MemoryTab
              personaId={personaId}
              stats={bundle.memoryStats}
              onStatsStale={() => void load()}
            />
          )}

          {tab === "archive" && (
            <ArchiveTab
              personaId={personaId}
              collections={bundle.collections}
              inherited={bundle.inheritedCollections}
              style={bundle.style}
              onCollectionsChanged={(next: MemoryCollection[]) =>
                setBundle((prev) => (prev ? { ...prev, collections: next } : prev))
              }
              onStyleChanged={(next: StyleProfile) =>
                setBundle((prev) => (prev ? { ...prev, style: next } : prev))
              }
            />
          )}

          {tab === "growth" && (
            <GrowthTab
              personaId={personaId}
              lessons={bundle.lessons}
              selfModel={persona.selfModel}
              pendingFeedback={bundle.pendingFeedback}
              onLessonsChanged={(next: GrowthLesson[]) =>
                setBundle((prev) => (prev ? { ...prev, lessons: next } : prev))
              }
              onSelfModelStale={() => void load()}
            />
          )}

          {tab === "history" && (
            <HistoryTab
              personaId={personaId}
              onReverted={() => {
                void load();
                onMutated();
              }}
              onError={setError}
            />
          )}
        </div>
      )}
    </Modal>
  );
}

function HeaderStrip({
  persona,
  unitPath,
  personaId,
  onAvatarChanged,
  onError,
  onCloned,
  onDeleted,
}: {
  persona: LibraryPersona;
  /** Kökten yaprağa çözünmüş birim yolu; boş ⇒ atanmamış. */
  unitPath: string;
  personaId: string;
  onAvatarChanged: (url: string) => void;
  onError: (msg: string) => void;
  onCloned: () => void;
  onDeleted: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const rate = approvalRate(persona.stats);

  async function upload(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    try {
      onAvatarChanged(await uploadAvatar(personaId, file));
    } catch (err) {
      onError(err instanceof LibraryApiError ? err.message : "Avatar yüklenemedi.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="flex flex-wrap items-start gap-4 rounded-xl border border-white/[0.07] bg-white/[0.02] p-3.5">
      <div className="relative">
        <PersonaAvatar
          url={persona.avatarUrl}
          name={persona.displayName}
          color={persona.themeColor}
          size={64}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={(e) => void upload(e.target.files?.[0])}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          title="Avatar yükle"
          className="absolute -bottom-1 -right-1 rounded-full border border-white/20 bg-[#0d1220] p-1 text-[10px] text-slate-300 transition-colors hover:border-violet-500/50 hover:text-white disabled:opacity-50"
        >
          {busy ? "…" : "📷"}
        </button>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <Chip tone="violet">{roleLabel(persona.role)}</Chip>
          <Chip>{persona.temperament}</Chip>
          <Chip>{persona.origin === "llm-synthesized" ? "model üretimi" : persona.origin}</Chip>
          {persona.isArchived && <Chip tone="rose">arşivde</Chip>}
        </div>
        <div
          className="mt-1.5 text-[11px] leading-snug text-slate-500"
          title={unitPath || undefined}
        >
          {unitPath ? `📁 ${unitPath}` : "○ Organizasyon birimine atanmamış"}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-slate-500">
          <span>{persona.stats.debateCount} tartışma</span>
          <span>{persona.stats.turnCount} tur</span>
          <span>{persona.stats.memoryCount} hafıza</span>
          {rate !== null && (
            <span className={rate >= 0.6 ? "text-emerald-400" : "text-amber-400"}>
              %{Math.round(rate * 100)} beğeni ({persona.stats.likeCount}/
              {persona.stats.likeCount + persona.stats.dislikeCount})
            </span>
          )}
          {persona.stats.ratingAvg !== null && (
            <span>ortalama {persona.stats.ratingAvg.toFixed(1)}/5</span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        <Btn
          size="sm"
          tone="ghost"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await clonePersona(personaId);
              onCloned();
            } catch (err) {
              onError(err instanceof LibraryApiError ? err.message : "Kopyalanamadı.");
            } finally {
              setBusy(false);
            }
          }}
        >
          Kopyala
        </Btn>
        <Btn
          size="sm"
          tone="ghost"
          disabled={busy}
          className="hover:text-rose-400"
          onClick={async () => {
            if (
              !confirm(
                `"${persona.title}" personası, tüm hafızası, belgeleri ve öğrendiği dersler kalıcı olarak silinsin mi? Bu geri alınamaz.`,
              )
            ) {
              return;
            }
            setBusy(true);
            try {
              await deletePersona(personaId);
              onDeleted();
            } catch (err) {
              onError(err instanceof LibraryApiError ? err.message : "Silinemedi.");
            } finally {
              setBusy(false);
            }
          }}
        >
          Sil
        </Btn>
      </div>
    </div>
  );
}

function IdentityTab({
  persona,
  catalog,
  units,
  personaId,
  onPatch,
  onError,
}: {
  persona: LibraryPersona;
  catalog: LibraryCatalog | null;
  units: PersonaUnit[];
  personaId: string;
  onPatch: (patch: Partial<LibraryPersona>) => void;
  onError: (msg: string) => void;
}) {
  const modelCatalog = useModelsCatalog();
  const [refining, setRefining] = useState(false);
  const [directive, setDirective] = useState("");
  const [busy, setBusy] = useState(false);
  const sampling = persona.samplingOverrides ?? {};
  const memoryCfg = persona.memoryConfig ?? {};

  async function runRefine() {
    setBusy(true);
    try {
      const res = await refineInstruction(personaId, directive.trim());
      if (res.changed) {
        onPatch({ systemInstruction: res.systemInstruction });
        setRefining(false);
        setDirective("");
      } else {
        onError("Model talimatı değiştirmedi; yönergeyi daha somut yazmayı deneyin.");
      }
    } catch (err) {
      onError(err instanceof LibraryApiError ? err.message : "Yeniden yazılamadı.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Kütüphane Başlığı" required>
          <TextInput
            value={persona.title}
            maxLength={120}
            onChange={(e) => onPatch({ title: e.target.value })}
          />
        </Field>
        <Field label="Görünen Ad" hint="Değiştirirsen hitap (Bey/Hanım) yeniden hesaplanır.">
          <TextInput
            value={persona.displayName}
            maxLength={60}
            onChange={(e) => onPatch({ displayName: e.target.value })}
          />
        </Field>
        <Field label="Mesleki Unvan" hint="Tartışmada birincil rozet olarak görünür.">
          <TextInput
            value={persona.expertise ?? ""}
            maxLength={80}
            onChange={(e) => onPatch({ expertise: e.target.value })}
          />
        </Field>
        <Field label="Bilişsel Duruş" hint="İkonu ve masadaki soyut rolü belirler.">
          <Select
            value={persona.role}
            onChange={(e) => onPatch({ role: e.target.value as LibraryPersona["role"] })}
          >
            {(catalog?.roles ?? [persona.role]).map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Field
        label="Organizasyon Birimi"
        hint={
          units.length === 0
            ? "Henüz birim yok. Kütüphane sayfasındaki ağaçtan bir kurum oluşturabilirsin."
            : "Personayı bir departman/birim altına yerleştirir; birimle toplu tartışma başlatmayı mümkün kılar."
        }
      >
        <Select
          value={persona.unitId ?? ""}
          disabled={units.length === 0}
          onChange={(e) => onPatch({ unitId: e.target.value || null })}
        >
          <option value="">— Atanmamış —</option>
          {units.map((u) => (
            <option key={u.id} value={u.id}>
              {"\u00A0".repeat(u.depth * 3)}
              {u.depth > 0 ? "└ " : ""}
              {u.name}
            </option>
          ))}
        </Select>
        {/* Seçili birimin TAM yolu. `<option>` içeriği girintiyle
            hiyerarşiyi ima ediyor ama uzun listede hangi kurumun altında
            olduğu kaybolabiliyor; çözünmüş zincir bunu tartışmasız kılar. */}
        {persona.unitId && (
          <p className="mt-1 rounded-md border border-white/[0.07] bg-black/20 px-2 py-1 text-[11px] leading-snug text-slate-400">
            📁 {unitPathLabel(units, persona.unitId) || "—"}
          </p>
        )}
      </Field>

      <Field label="Kısa Tanıtım">
        <TextArea
          rows={2}
          value={persona.summary}
          maxLength={600}
          onChange={(e) => onPatch({ summary: e.target.value })}
        />
      </Field>

      <Field
        label="Değişmez Görev Tanımı"
        hint="Her tartışmada system prompt'un EN BAŞINA konur; konu ne olursa olsun geçerlidir."
      >
        <TextArea
          rows={4}
          value={persona.charter}
          maxLength={1200}
          onChange={(e) => onPatch({ charter: e.target.value })}
        />
      </Field>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12px] font-medium text-slate-300">Konuşma Talimatı</span>
          <Btn size="sm" tone="outline" onClick={() => setRefining((v) => !v)}>
            Yapay zekâ ile ince ayar
          </Btn>
        </div>
        {refining && (
          <div className="space-y-2 rounded-lg border border-violet-500/25 bg-violet-500/[0.05] p-2.5">
            <TextInput
              value={directive}
              autoFocus
              maxLength={600}
              placeholder="ör. Tedarikçi iddialarına karşı daha şüpheci olsun, her sayı için kaynak istesin."
              onChange={(e) => setDirective(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && directive.trim().length > 3) void runRefine();
              }}
            />
            <div className="flex items-center gap-1.5">
              <Btn
                size="sm"
                tone="primary"
                loading={busy}
                disabled={directive.trim().length < 4}
                onClick={runRefine}
              >
                Uygula
              </Btn>
              <span className="text-[11px] text-slate-500">
                Kimliği korur, yalnız istediğin davranışı değiştirir. Sonucu kaydetmeden
                görebilirsin.
              </span>
            </div>
          </div>
        )}
        <TextArea
          rows={9}
          value={persona.systemInstruction}
          maxLength={4000}
          onChange={(e) => onPatch({ systemInstruction: e.target.value })}
        />
      </div>

      <details className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3.5">
        <summary className="cursor-pointer text-[12.5px] font-medium text-slate-300">
          Model ve örnekleme ayarları
        </summary>
        <div className="mt-3 space-y-3">
          {/* Serbest metin kutusuydu: operatörün model kimliğini ezberden
              doğru yazması bekleniyordu ve bir harf hatası sessizce
              varsayılana düşüyordu. Katalogdan seçmek hem yazım hatasını
              hem "hangi modeller var?" sorusunu ortadan kaldırıyor. */}
          <Field
            label="Model"
            hint={
              modelCatalog.status === "error"
                ? "Model listesi alınamadı — mevcut değer korunuyor."
                : "Boş bırakılırsa orkestratörün varsayılan modeli kullanılır."
            }
          >
            <Select
              value={persona.modelId ?? ""}
              disabled={modelCatalog.status !== "ready"}
              onChange={(e) => onPatch({ modelId: e.target.value || null })}
            >
              <option value="">
                {modelCatalog.status === "loading"
                  ? "Yükleniyor…"
                  : "Varsayılan (konsey dağılımı)"}
              </option>
              {modelCatalog.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
              {/* Katalogda olmayan eski bir değer kayıtlıysa kaybolmasın. */}
              {persona.modelId &&
              !modelCatalog.models.some((m) => m.id === persona.modelId) ? (
                <option value={persona.modelId}>
                  {persona.modelId} (katalogda yok)
                </option>
              ) : null}
            </Select>
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={`Sıcaklık: ${sampling.temperature ?? "varsayılan"}`}
              hint="Düşük = tutarlı ve temkinli, yüksek = yaratıcı ve dağınık."
            >
              <input
                type="range"
                min={0}
                max={2}
                step={0.05}
                value={sampling.temperature ?? 0.55}
                onChange={(e) =>
                  onPatch({
                    samplingOverrides: {
                      ...sampling,
                      temperature: Number(e.target.value),
                    },
                  })
                }
                className="w-full accent-violet-500"
              />
            </Field>
            <Field label={`En fazla token: ${sampling.maxTokens ?? "varsayılan"}`}>
              <input
                type="range"
                min={128}
                max={4096}
                step={64}
                value={sampling.maxTokens ?? 1100}
                onChange={(e) =>
                  onPatch({
                    samplingOverrides: { ...sampling, maxTokens: Number(e.target.value) },
                  })
                }
                className="w-full accent-violet-500"
              />
            </Field>
          </div>
          <Btn
            size="sm"
            tone="ghost"
            onClick={() => onPatch({ samplingOverrides: {} })}
            disabled={Object.keys(sampling).length === 0}
          >
            Örnekleme ayarlarını temizle
          </Btn>
        </div>
      </details>

      <details className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3.5">
        <summary className="cursor-pointer text-[12.5px] font-medium text-slate-300">
          Hafıza davranışı
        </summary>
        <div className="mt-3 space-y-3">
          <Toggle
            checked={memoryCfg.autoCapture !== false}
            onChange={(v) => onPatch({ memoryConfig: { ...memoryCfg, autoCapture: v } })}
            label="Tartışma sonrası otomatik hafıza yaz"
            hint="Kapatılırsa bu persona hiçbir tartışmayı hatırlamaz."
          />
          <Toggle
            checked={memoryCfg.useDocuments !== false}
            onChange={(v) => onPatch({ memoryConfig: { ...memoryCfg, useDocuments: v } })}
            label="Kişisel arşivinden alıntı yapabilsin"
          />
          <Toggle
            checked={memoryCfg.useLessons !== false}
            onChange={(v) => onPatch({ memoryConfig: { ...memoryCfg, useLessons: v } })}
            label="Öğrendiği dersleri uygulasın"
          />
          <Field
            label={`Tur başına hatırlanacak not: ${memoryCfg.topK ?? "varsayılan (6)"}`}
            hint="Yüksek değer daha çok bağlam ama daha uzun prompt demektir."
          >
            <input
              type="range"
              min={0}
              max={15}
              value={memoryCfg.topK ?? 6}
              onChange={(e) =>
                onPatch({ memoryConfig: { ...memoryCfg, topK: Number(e.target.value) } })
              }
              className="w-full accent-violet-500"
            />
          </Field>
        </div>
      </details>

      <Toggle
        checked={persona.isArchived}
        onChange={(v) => onPatch({ isArchived: v })}
        label="Arşivle"
        hint="Arşivlenen persona listede gizlenir ve yeni tartışmalarda önerilmez; hafızası korunur."
      />
    </div>
  );
}

function HistoryTab({
  personaId,
  onReverted,
  onError,
}: {
  personaId: string;
  onReverted: () => void;
  onError: (msg: string) => void;
}) {
  const [revisions, setRevisions] = useState<PersonaRevision[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setRevisions(await listRevisions(personaId));
      } catch (err) {
        onError(err instanceof LibraryApiError ? err.message : "Geçmiş yüklenemedi.");
      }
    })();
  }, [personaId, onError]);

  return (
    <div className="space-y-3">
      <SectionTitle
        title="Düzenleme Geçmişi"
        hint="Her kayıt bir sürüm oluşturur. Geri dönmek eskiyi silmez — yeni bir sürüm olarak yazılır."
      />
      {revisions.length === 0 ? (
        <p className="py-4 text-[12px] text-slate-600">Henüz sürüm kaydı yok.</p>
      ) : (
        <div className="space-y-1.5">
          {revisions.map((r) => (
            <div
              key={r.revision}
              className="flex items-start gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2"
            >
              <Chip tone="violet">v{r.revision}</Chip>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] text-slate-300">
                  {r.changeNote || "Değişiklik notu yok"}
                </div>
                <div className="text-[10.5px] text-slate-600">
                  {new Date(r.createdAt).toLocaleString("tr-TR")} · {r.changedBy}
                </div>
              </div>
              <Btn
                size="sm"
                tone="ghost"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await revertRevision(personaId, r.revision);
                    onReverted();
                  } catch (err) {
                    onError(err instanceof LibraryApiError ? err.message : "Geri alınamadı.");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Bu sürüme dön
              </Btn>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
